import { ReactNode } from 'react';
import { AlertColor } from '@mui/material';
import * as Icons from '../icons';
import { DefaultAdditionalEventProperties, Event } from './Timeline.types';

export const DATE_KEY_FORMAT = 'yyyy-MM-dd';
export const DATE_HEADER_FORMAT = 'EEEE, dd MMM yyyy';
export const DATE_TIME_FORMAT = 'dd MMM \u00A0 HH:mm:ss';

const ALERT_COLORS = ['info', 'warning', 'success', 'error'];

export const DEFAULT_ICON_MAP = {
  info: <Icons.InfoOutlined />,
  success: <Icons.Check />,
  warning: <Icons.Warning />,
  error: <Icons.ErrorOutline />,
  unknown: <Icons.BatteryUnknown />,
};

export const DEFAULT_SOURCE_ICON_MAP = {
  greenerwatch: <Icons.WatcherType sx={{ fontSize: 16, color: 'grey.800' }} />,
  greenerhmi: <Icons.Tablet sx={{ fontSize: 16, color: 'grey.800' }} />,
  telegram: <Icons.TelegramIcon width={16} height={16} />,
  unknown: null,
};

export const DEFAULT_SOURCE_LABEL_MAP: Record<string, string> = {
  greenerwatch: 'Greener Watch',
  greenerhmi: 'Greener HMI',
};

export function isDefaultEventProperties<T extends object>(
  event: Event<T>
): event is Event<T> & DefaultAdditionalEventProperties {
  return 'dtTimestamp' in event && 'user' in event;
}

export const convertTimestamp = (timestamp: number | string) => {
  if (typeof timestamp === 'number' && timestamp > 1e15) {
    return Number(BigInt(timestamp) / BigInt(1_000_000));
  }
  if (typeof timestamp === 'number') {
    const length = timestamp.toString().length;

    // Unix timestamp (seconds)
    if (length === 10) {
      return timestamp * 1000;
    } else if (length === 13) {
      // Timestamp in milliseconds
      return timestamp;
    } else if (length === 16) {
      // Timestamp in microseconds
      return timestamp / 1000;
    }
  }
  return timestamp;
};

export const defaultGetDerivedSeverity = <T extends object>(event: Event<T>) => {
  const { type, severity } = event ?? {};
  const normalizedSeverity = severity.toLowerCase();

  // Check if severity is not one of the expected values and return 'info'
  if (!ALERT_COLORS.includes(normalizedSeverity)) {
    return 'info';
  }

  // Additional condition for transforming severity 'info' to 'success' based on the type
  if (normalizedSeverity === 'info' && type?.toLowerCase().includes('command')) {
    return 'success';
  }

  return normalizedSeverity as AlertColor;
};

export const defaultRenderEventTitle = <T extends object>(event: Event<T>) => {
  if (event.title) {
    return event.title;
  }
  const { severity } = event;

  switch (severity) {
    case 'info':
      return 'Info';
    case 'success':
      return 'Success';
    case 'warning':
      return 'Warning';
    case 'error':
      return 'Error';
    default:
      return null;
  }
};

export const defaultRenderEventContent = <T extends object>(event: Event<T>) =>
  event.msg ?? 'This event has no description.';

export const defaultIconRender = <T extends object>(iconMapping: Record<string, ReactNode>, event: Event<T>) => {
  const severity = event?.severity?.toLowerCase();
  // Check if the source is in iconMapping or default to "unknown"
  return severity in iconMapping ? iconMapping[severity] : iconMapping['unknown'];
};

export const defaultGetTimestamp = <T extends object>(event: Event<T>) => event.timestamp;

export const defaultSourceIconRender = <T extends object>(
  sourceIconMapping: Record<string, ReactNode>,
  event: Event<T>
) => {
  const source = event?.source?.toLowerCase();
  return source in sourceIconMapping ? sourceIconMapping[source] : sourceIconMapping['unknown'];
};

export const getSourceLabel = <T extends object>({
  event,
  sourceLabelMapping,
}: {
  event: Event<T>;
  sourceLabelMapping: Record<string, string>;
}): string => {
  const source = event?.source?.toLowerCase();
  return sourceLabelMapping[source] ?? event?.source ?? 'unknown';
};
